/**
 * Store + Format Groups Types and Validation
 * 
 * AUTHORING-ONLY CONSTRUCTS — DO NOT USE IN:
 * - Task generation
 * - Mobile payloads
 * - PDF/Excel exports
 * - Snapshot structures (post-lock)
 * 
 * Only the lock-time compiler may consume group structure.
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Format entry within a group - specifies format and quantity per-store
 */
export interface GroupFormatEntry {
    mediaFormatId: number;
    quantity: number; // Must be >= 1, applied per-store when flattened
    // For limited-capacity formats (POPAds): coverage percentage (25, 50, 75, 100)
    coveragePercent?: number | null;
}

/**
 * Store + Format Group - authoring-only container
 * 
 * These groups exist during draft phase only and are flattened
 * into store×format rows at lock time.
 */
export interface StoreFormatGroup {
    groupId: string; // client-generated UUID
    groupName: string;
    storeIds: number[];
    formatEntries: GroupFormatEntry[];
    operatorTaskId?: number | null; // Operator task for payment rate - required before confirm
    // Per-store coverage overrides for POPAd groups (storeId -> coveragePercent)
    storeCoverageOverrides?: Record<number, number>;
    labelId?: number | null; // Optional retailer label for organizing site lists
    // Option 2: Per-group posting instructions (overrides campaign-level if set)
    postingInstructionsText?: string | null;
    postingInstructionsImageUrl?: string | null;
    instoreLocationId?: number | null;
    instoreLocationCustomText?: string | null;
    // Point-in-time operator assignment captured at group authoring (e.g. Excel import).
    // When present, the lock-time snapshot prefers these over store_format_operator_assignments
    // for any (storeId, mediaFormatId) pair listed here. operatorId === 0 means intentionally unassigned.
    operatorAssignments?: Array<{
        storeId: number;
        mediaFormatId: number;
        operatorId: number;
    }>;
}

/**
 * Validation result from group validation
 */
export interface GroupValidationResult {
    valid: boolean;
    errors: GroupValidationError[];
    warnings: GroupValidationWarning[];
}

export interface GroupValidationError {
    type: 'OVERLAP' | 'EMPTY_GROUP' | 'INVALID_QUANTITY' | 'FORMAT_UNAVAILABLE' | 'COVERAGE_MISMATCH' | 'MISSING_OPERATOR_TASK';
    message: string;
    groupId?: string;
    storeId?: number;
    mediaFormatId?: number;
}

export interface GroupValidationWarning {
    type: 'OPERATOR_MISSING' | 'CAPACITY_LOW';
    message: string;
    groupId?: string;
    storeId?: number;
    mediaFormatId?: number;
}

/**
 * Preview stats for a single group
 */
export interface GroupPreviewStats {
    storeCount: number;
    formatCount: number;
    assignmentCount: number;
    unitCount: number;
}

/**
 * Total preview stats across all groups
 */
export interface TotalPreviewStats {
    groupCount: number;
    totalStores: number;
    totalFormats: number;
    totalAssignments: number;
    totalUnits: number;
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates that no (storeId, mediaFormatId) pair appears in more than one group
 * for limited-capacity formats.
 *
 * Unlimited formats are allowed to overlap across groups during validation.
 */
export function validateNoOverlap(
    groups: StoreFormatGroup[],
    limitedFormatIds: Set<number> = new Set(),
): GroupValidationResult {
    const seen = new Map<string, { groupId: string; groupName: string }>();
    const errors: GroupValidationError[] = [];

    for (const group of groups) {
        for (const storeId of group.storeIds) {
            for (const { mediaFormatId } of group.formatEntries) {
                if (!limitedFormatIds.has(mediaFormatId)) {
                    continue;
                }

                const key = `${storeId}|${mediaFormatId}`;

                if (seen.has(key)) {
                    const existing = seen.get(key)!;
                    errors.push({
                        type: 'OVERLAP',
                        message: `Store ${storeId} × Format ${mediaFormatId} appears in both "${group.groupName}" and "${existing.groupName}"`,
                        groupId: group.groupId,
                        storeId,
                        mediaFormatId,
                    });
                } else {
                    seen.set(key, { groupId: group.groupId, groupName: group.groupName });
                }
            }
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings: [],
    };
}

/**
 * Validates that each group has at least one store and one format.
 */
export function validateNonEmptyGroups(groups: StoreFormatGroup[]): GroupValidationResult {
    const errors: GroupValidationError[] = [];

    for (const group of groups) {
        if (group.storeIds.length === 0) {
            errors.push({
                type: 'EMPTY_GROUP',
                message: `Group "${group.groupName}" has no stores`,
                groupId: group.groupId,
            });
        }

        if (group.formatEntries.length === 0) {
            errors.push({
                type: 'EMPTY_GROUP',
                message: `Group "${group.groupName}" has no formats`,
                groupId: group.groupId,
            });
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings: [],
    };
}

/**
 * Validates that all quantities are >= 1.
 */
export function validateQuantities(groups: StoreFormatGroup[]): GroupValidationResult {
    const errors: GroupValidationError[] = [];

    for (const group of groups) {
        for (const entry of group.formatEntries) {
            if (!Number.isInteger(entry.quantity) || entry.quantity < 1) {
                errors.push({
                    type: 'INVALID_QUANTITY',
                    message: `Group "${group.groupName}": Format ${entry.mediaFormatId} has invalid quantity ${entry.quantity} (must be integer >= 1)`,
                    groupId: group.groupId,
                    mediaFormatId: entry.mediaFormatId,
                });
            }
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings: [],
    };
}

/**
 * Validates that all mediaFormatIds are valid (positive integers).
 * This catches placeholder values (0) that haven't been filled in.
 */
export function validateMediaFormatIds(groups: StoreFormatGroup[]): GroupValidationResult {
    const errors: GroupValidationError[] = [];

    for (const group of groups) {
        for (const entry of group.formatEntries) {
            if (!Number.isInteger(entry.mediaFormatId) || entry.mediaFormatId < 1) {
                errors.push({
                    type: 'INVALID_QUANTITY', // Reuse type - could add INVALID_FORMAT_ID if needed
                    message: `Group "${group.groupName}": Invalid media format (format must be selected)`,
                    groupId: group.groupId,
                    mediaFormatId: entry.mediaFormatId,
                });
            }
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings: [],
    };
}

/**
 * Checks if a group uses limited-capacity (POPAd) formats.
 * POPAd groups are identified by having coveragePercent set on format entries.
 */
export function isPopadGroup(group: StoreFormatGroup): boolean {
    return group.formatEntries.some(entry => 
        entry.coveragePercent != null && entry.coveragePercent > 0
    );
}

/**
 * Validates that each group has an operator task selected.
 * All groups (including POPAd) require operator tasks for payment rates.
 */
export function validateOperatorTasks(groups: StoreFormatGroup[]): GroupValidationResult {
    const errors: GroupValidationError[] = [];

    for (const group of groups) {
        if (!group.operatorTaskId || !Number.isFinite(group.operatorTaskId)) {
            errors.push({
                type: 'MISSING_OPERATOR_TASK',
                message: `Group "${group.groupName}" requires an operator task for payment rates`,
                groupId: group.groupId,
            });
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings: [],
    };
}

/**
 * Runs all client-side validations on groups.
 * 
 * This combines non-empty, quantity, format ID, and operator task checks.
 * Overlap, format availability, and operator coverage require server-side data.
 */
export function validateGroupsClient(groups: StoreFormatGroup[]): GroupValidationResult {
    const emptyResult = validateNonEmptyGroups(groups);
    const quantityResult = validateQuantities(groups);
    const formatIdResult = validateMediaFormatIds(groups);
    const operatorTaskResult = validateOperatorTasks(groups);

    const allErrors = [
        ...emptyResult.errors,
        ...quantityResult.errors,
        ...formatIdResult.errors,
        ...operatorTaskResult.errors,
    ];

    const allWarnings = [
        ...emptyResult.warnings,
        ...quantityResult.warnings,
        ...formatIdResult.warnings,
        ...operatorTaskResult.warnings,
    ];

    return {
        valid: allErrors.length === 0,
        errors: allErrors,
        warnings: allWarnings,
    };
}

// ============================================================================
// Flattening Functions
// ============================================================================

/**
 * Flattened assignment row - NO group metadata.
 * This is what goes into snapshots after lock.
 */
export interface FlattenedAssignmentRow {
    storeId: number;
    mediaFormatId: number;
    quantity: number;
    operatorTaskId?: number | null; // Operator task for payment rate
    coveragePercent?: number | null; // For limited-capacity formats (POPAds)
    // NO groupId, NO groupName
}

/**
 * Flattens groups into store×format rows.
 * 
 * CRITICAL: The output contains NO group IDs or group names.
 * This function produces the source of truth for snapshot creation.
 * 
 * @param groups - Array of StoreFormatGroup to flatten
 * @returns Array of flattened assignment rows
 */
export function flattenGroups(groups: StoreFormatGroup[]): FlattenedAssignmentRow[] {
    const rows: FlattenedAssignmentRow[] = [];

    for (const group of groups) {
        // Defensive: ensure storeIds and formatEntries are arrays
        const storeIds = Array.isArray(group.storeIds) ? group.storeIds : [];
        const formatEntries = Array.isArray(group.formatEntries) ? group.formatEntries : [];
        
        for (const storeId of storeIds) {
            for (const entry of formatEntries) {
                const { mediaFormatId, quantity, coveragePercent } = entry || {};
                // Use per-store override if available, otherwise use format-level coverage
                // Defensive: ensure storeCoverageOverrides is an object before accessing
                const overrides = group.storeCoverageOverrides && typeof group.storeCoverageOverrides === 'object' 
                    ? group.storeCoverageOverrides 
                    : {};
                const storeCoverage = overrides[storeId] ?? coveragePercent;
                rows.push({
                    storeId,
                    mediaFormatId,
                    quantity,
                    operatorTaskId: group.operatorTaskId ?? null,
                    coveragePercent: storeCoverage ?? null,
                    // NO groupId, NO groupName - critical invariant
                });
            }
        }
    }

    return rows;
}

/**
 * Calculates preview statistics for a group.
 */
export function calculateGroupPreview(group: StoreFormatGroup): {
    storeCount: number;
    formatCount: number;
    unitCount: number;
    assignmentCount: number;
} {
    const storeCount = group.storeIds.length;
    const formatCount = group.formatEntries.length;
    const unitCount = group.formatEntries.reduce(
        (sum, f) => sum + f.quantity * storeCount,
        0
    );
    const assignmentCount = storeCount * formatCount;

    return { storeCount, formatCount, unitCount, assignmentCount };
}

/**
 * Calculates total preview for all groups.
 */
export function calculateTotalPreview(groups: StoreFormatGroup[]): {
    groupCount: number;
    totalStores: number;
    totalFormats: number;
    totalUnits: number;
    totalAssignments: number;
} {
    const uniqueStores = new Set<number>();
    const uniqueFormats = new Set<number>();
    let totalUnits = 0;
    let totalAssignments = 0;

    for (const group of groups) {
        group.storeIds.forEach(id => uniqueStores.add(id));
        group.formatEntries.forEach(f => uniqueFormats.add(f.mediaFormatId));

        const preview = calculateGroupPreview(group);
        totalUnits += preview.unitCount;
        totalAssignments += preview.assignmentCount;
    }

    return {
        groupCount: groups.length,
        totalStores: uniqueStores.size,
        totalFormats: uniqueFormats.size,
        totalUnits,
        totalAssignments,
    };
}

// ============================================================================
// Assertion Functions (for use in snapshot services)
// ============================================================================

/**
 * Asserts that a snapshot object contains no group metadata.
 * 
 * CRITICAL: This must be called before persisting any snapshot to ensure
 * the core invariant is maintained.
 * 
 * @throws Error if any forbidden fields are found
 */
export function assertNoGroupsInSnapshot(snapshot: unknown): void {
    const forbidden = ['groupId', 'groupName', 'group_id', 'group_name'];
    const json = JSON.stringify(snapshot);

    for (const field of forbidden) {
        if (json.includes(`"${field}"`)) {
            throw new Error(
                `INVARIANT VIOLATION: Snapshot contains "${field}". ` +
                `Store + Format Groups must be flattened before snapshot creation.`
            );
        }
    }
}
